import type { FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';
import cx from 'classnames';
import { formatDate } from 'date-fns';

import { forTemplate } from '@trello/legacy-i18n';
import { CollaboratorOrMemberAvatar } from '@trello/member-avatar';
import type { PIIString } from '@trello/privacy';
import type { TeamTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { AddToWorkspaceButton } from 'app/src/components/AddGuestToWorkspace';
import { WorkspaceMemberChangeRoleButton } from 'app/src/components/WorkspaceMemberChangeRoleButton';
import { WorkspaceMemberLeaveButton } from 'app/src/components/WorkspaceMemberLeaveButton';
import { WorkspaceMemberReactivateButton } from 'app/src/components/WorkspaceMemberReactivateButton';
import { WorkspaceMemberRestrictedPermissionButton } from 'app/src/components/WorkspaceMemberRestrictedPermissionButton';
import { RemoveMemberFromWorkspaceButton } from './RemoveMemberFromWorkspaceButton';
import { WorkspaceBoards } from './WorkspaceBoards';

import styles from './WorkspaceMemberItem.less';

//we need to keep this because intl.formatMessage doesn't appear to support shouldEscapeStrings: false
const format = forTemplate('select_member', {
  shouldEscapeStrings: false,
});

interface WorkspaceMemberItemProps {
  allowReactivate: boolean;
  allowTypeChange: boolean;
  fullName: PIIString;
  id: string;
  idEnterprise: string;
  isDeactivated: boolean;
  isMe: boolean;
  isOrganizationAdmin: boolean;
  isOrgMember: boolean;
  isPending: boolean;
  lastActive?: string | null;
  orgBillingUrl?: string;
  orgEnterpriseName?: string;
  orgIdEnterprise?: string;
  orgIsBC: boolean;
  orgMemberType: string;
  ownsOrg: boolean;
  username: string;
  workspaceId: string;
}

export const WorkspaceMemberItem: FunctionComponent<
  WorkspaceMemberItemProps
> = ({
  allowReactivate,
  allowTypeChange,
  fullName,
  id,
  idEnterprise,
  isDeactivated,
  isMe,
  isOrganizationAdmin,
  isOrgMember,
  isPending,
  lastActive,
  orgBillingUrl,
  orgEnterpriseName,
  orgIdEnterprise,
  orgIsBC,
  orgMemberType,
  ownsOrg,
  username,
  workspaceId,
}) => {
  const shouldShowLeaveButton = isOrgMember && isMe;

  const lastActiveDate =
    lastActive && formatDate(new Date(lastActive), 'MMMM yyyy');

  return (
    <div
      className={styles.memberListItemDetail}
      data-testid={getTestId<TeamTestIds>('workspace-member-item')}
    >
      <CollaboratorOrMemberAvatar
        idMember={id}
        isGhost={isPending}
        deactivated={isDeactivated}
        workspaceAdmin={isOrganizationAdmin}
      />
      <div className={styles.details}>
        <p>
          <strong>{fullName}</strong>
        </p>
        <div className={styles.userNameAndLastActiveDate}>
          <p>
            {username && `@${username}`}
            &nbsp;
            {lastActiveDate && (
              <span>
                <span className={styles.bulletSeparator}>&#x2022;</span>
                <FormattedMessage
                  id="templates.member_list_item_detail.last-active"
                  defaultMessage="Last active {lastActiveDate}"
                  description="The date the Workspace member was last active within the Workspace"
                  values={{
                    lastActiveDate,
                  }}
                />
              </span>
            )}
            {!lastActiveDate && (
              <span>
                <span className={styles.bulletSeparator}>&#x2022;</span>
                <FormattedMessage
                  id="templates.member_list_item_detail.no-recent-activity"
                  defaultMessage="No recent activity"
                  description="User has not been active in Trello recently"
                />
              </span>
            )}
          </p>
          {orgEnterpriseName && (
            <p>
              {orgIdEnterprise === idEnterprise
                ? format('managed-by-enterprise', {
                    enterpriseName: orgEnterpriseName,
                  })
                : format('not-managed-by-enterprise', {
                    enterpriseName: orgEnterpriseName,
                  })}
            </p>
          )}
        </div>
      </div>

      <div className={styles.options}>
        <WorkspaceBoards idMember={id} idOrganization={workspaceId} />
        {isOrgMember && !orgIsBC && (
          <WorkspaceMemberRestrictedPermissionButton
            orgBillingUrl={orgBillingUrl ?? ''}
            orgMemberType={orgMemberType}
            orgId={workspaceId}
          />
        )}
        {isOrgMember && orgIsBC && allowTypeChange && (
          <WorkspaceMemberChangeRoleButton
            membershipType={orgMemberType}
            workspaceId={workspaceId}
            memberId={id}
            isWorkspaceAdmin={isOrganizationAdmin}
          />
        )}
        {isOrgMember && orgIsBC && !allowTypeChange && (
          <p className={cx(styles.orgMemberType, styles.option)}>
            {orgMemberType}
          </p>
        )}
        {isOrgMember && allowReactivate && (
          <WorkspaceMemberReactivateButton
            workspaceId={workspaceId}
            memberId={id}
          />
        )}
        {shouldShowLeaveButton && (
          <WorkspaceMemberLeaveButton workspaceId={workspaceId} memberId={id} />
        )}
        {isOrgMember && !isMe && ownsOrg && (
          <RemoveMemberFromWorkspaceButton
            idMember={id}
            idOrganization={workspaceId}
          />
        )}
        {!isOrgMember && (
          <>
            {isDeactivated && (
              <p className={cx(styles.orgMemberType, styles.option)}>
                <FormattedMessage
                  id="templates.member_list_item_detail.deactivated"
                  description="User is deactivated"
                  defaultMessage="Deactivated"
                />
              </p>
            )}
            {allowTypeChange && (
              <AddToWorkspaceButton memberId={id} workspaceId={workspaceId} />
            )}
          </>
        )}
      </div>
    </div>
  );
};
